import { Component } from 'react';
import { Modal } from 'react-bootstrap';
import { inject } from 'mobx-react';

//components
import withLocalization from '~/hoc/withLocalization';
import Button from '../../../components/CustomButton/CustomButton.jsx';

//elements
import LoadingSpinner from '../../../elements/LoadingSpinner.js';
import { SimpleSelectWidget } from '../../../components/GenericForm/Widgets/SimpleSelect.js';
import Input from '../../../elements/Input.js';
import RequiredStar from '../../../elements/RequiredStar.js';

//assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';

//config
import { SAMPLE_FILES_ROOT } from '~/library/config';

//utils
import getBase64 from '~/utils/getBase64';
import { importSVG } from '~/utils/constants.js';

const SAMPLE_FILE_NAME = 'user_timesheets_import_sample_file.xlsx';

class ImportTimelogsModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showImportModal: false,
            file: null,
            importing: false,
            message: '',
            clientId: null,
            secretKey: '',
            clientOptions: []
        };
        this.fileInputRef = null;
    }

    handleClose = () => {
        this.setState({
            file: null,
            showImportModal: false,
            importing: false,
            message: '',
            clientId: null,
            secretKey: '',
            clientOptions: []
        });
    }

    handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        this.setState({ file: selectedFile });
    }

    handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { t, commonStore } = this.props;
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.name.endsWith('.xlsx')) {
                this.setState({ file });
            } else {
                commonStore.addNotification(t('Only Excel (.xlsx) files are allowed'), null, 'error');
            }
        }
    }

    handleUpload = async () => {
        try {
            const { file, clientId, secretKey } = this.state;
            const { t, clientStore, commonStore } = this.props;

            if (!secretKey || !secretKey.trim())
                return commonStore.addNotification(t('Please enter the secret key'), null, 'warning');

            if (!clientId)
                return commonStore.addNotification(t('Please select a client'), null, 'warning');

            if (!file)
                return commonStore.addNotification(t('Please select a file'), null, 'warning');

            const base64 = await getBase64(file);
            if (!base64)
                return commonStore.addNotification(t('Failed to read file'), null, 'error');

            this.setState({ importing: true });

            const res = await clientStore.importTimesheetFromExcel({
                fileBase64: base64,
                clientId: clientId,
                secretKey: secretKey.trim()
            });

            if (res) {
                this.setState({
                    importing: false,
                    message: res.message || 'Import in process'
                });
            }
        } catch (error) {
            this.setState({ file: null, importing: false, message: '' });
            console.error({ error });
        }
    }

    render() {
        const { t } = this.props;
        const { showImportModal, importing, message, clientId, file, secretKey, clientOptions } = this.state;

        return (
            <div>
                <Button
                    fill
                    dynamic_lg
                    disabled={this.props.clientStore.loading}
                    onClick={() => this.setState({
                        showImportModal: true,
                        clientOptions: this.props.clientStore.currentList.rows.map(c => ({ value: c.id, label: c.name }))
                    })}
                >
                    {importSVG({ width: 24, fillColor: '#ffffff' })}
                    {t('Import Timelogs')}
                </Button>

                <Modal size="lg" show={showImportModal} centered>
                    <Modal.Header>
                        <Modal.Title className="w-100 text-center ApproveModal__title m-0">
                            {t('Import Timelogs from Excel')}
                        </Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            alt="close icon"
                            onClick={this.handleClose}
                        />
                    </Modal.Header>

                    <Modal.Body className="my-0" style={{ minHeight: '250px' }}>
                        {importing ? (
                            <div className="d-flex justify-content-center align-items-center h-100">
                                <div className="fw-bold fs-5"><LoadingSpinner /></div>
                            </div>
                        ) : message ? (
                            <div className="d-flex flex-column justify-content-center align-items-center h-100 gap-3">
                                <i className="fa fa-check-circle text-success" style={{ fontSize: '48px' }} />
                                <div className="fw-bold fs-5 text-center">
                                    {t('Import in process, you will be notified by email once completed')}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="mb-3 d-flex justify-content-between align-items-center">
                                    <span className="fw-600">{t('Upload')} {t('Excel File')}</span>
                                    <Button
                                        fill
                                        dynamic_lg
                                        onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = `${SAMPLE_FILES_ROOT}/${SAMPLE_FILE_NAME}`;
                                            link.download = SAMPLE_FILE_NAME;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        }}
                                    >
                                        {t('Download')} {t('Sample File')}
                                    </Button>
                                </div>

                                <div className="mb-3">
                                    <label className="control-label fw-600 mb-1">
                                        {t('Secret Key')} <RequiredStar />
                                    </label>
                                    <Input
                                        type="password"
                                        name="secretKey"
                                        value={secretKey}
                                        placeholder={t('Enter secret key')}
                                        onChange={(e) => this.setState({ secretKey: e.target.value })}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="control-label fw-600 mb-1">
                                        {t('Select Client')} <RequiredStar />
                                    </label>
                                    <SimpleSelectWidget
                                        options={clientOptions}
                                        placeholder={t('Select Client')}
                                        value={clientId}
                                        onChange={(val) => this.setState({ clientId: val })}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="control-label fw-600 mb-1">
                                        {t('Upload Excel File')} <RequiredStar />
                                    </label>
                                    <div
                                        className="attachment-box"
                                        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        onDrop={this.handleDrop}
                                        onClick={() => this.fileInputRef && this.fileInputRef.click()}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <input
                                            ref={(ref) => (this.fileInputRef = ref)}
                                            type="file"
                                            style={{ display: 'none' }}
                                            accept=".xlsx"
                                            onChange={this.handleFileChange}
                                        />
                                        {!file ? (
                                            <p className="m-0 text-muted">
                                                {t('Drag and drop Excel file here, or click to upload')}
                                            </p>
                                        ) : (
                                            <p className="m-0 fw-bold">
                                                {t('Selected file')}: {file.name}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="text-muted" style={{ fontSize: '12px' }}>
                                    <span className="fw-600">{t('Expected columns')}: </span>
                                    {t('Date (DD.MM.YYYY), Employee Number, From (HH:MM), To (HH:MM), Total (decimal e.g. 7.5), Project Number')}
                                </div>
                            </div>
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        {!message && (
                            <Button
                                fill
                                dynamic_lg
                                me2
                                disabled={importing || !file || !clientId || !secretKey}
                                onClick={this.handleUpload}
                            >
                                {t('Upload')}
                            </Button>
                        )}
                        <Button variant="secondary" onClick={this.handleClose}>
                            {t('Close')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

export default inject('commonStore', 'clientStore')(withLocalization(ImportTimelogsModal));